// Three.js scene, camera, lights and sky. Reads day/night state each frame to
// move the sun and recolour the sky. FOV and fog distance follow config so the
// settings menu can change them live.

import * as THREE from 'three';
import { config } from '../sim/config';
import { PLAYER_EYE } from '../sim/constants';
import { SkyState } from '../sim/daynight';
import { Vec3 } from '../sim/math';
import { HeldItem } from './heldItem';

const DAY_SKY = new THREE.Color(0x8ec5ff);
const NIGHT_SKY = new THREE.Color(0x0a0e1a);
const DAY_FOG = new THREE.Color(0xbfe0ff);
const NIGHT_FOG = new THREE.Color(0x0a0e1a);

export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private sun: THREE.DirectionalLight;
  private ambient: THREE.HemisphereLight;
  private fog: THREE.Fog;
  private held: HeldItem;
  private tmp = new THREE.Color();

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(
      config.fov,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );

    this.fog = new THREE.Fog(DAY_FOG.getHex(), 0, 100);
    this.scene.fog = this.fog;

    this.ambient = new THREE.HemisphereLight(0xffffff, 0x556677, 1.0);
    this.scene.add(this.ambient);

    this.sun = new THREE.DirectionalLight(0xffffff, 1.4);
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    // Add the camera to the scene so its child (the held-item viewmodel) renders.
    this.scene.add(this.camera);
    this.held = new HeldItem(this.camera);

    window.addEventListener('resize', () => this.onResize());
  }

  /** Show the given item in first person (null = empty hand). */
  setHeldItem(id: number | null): void {
    this.held.setItem(id);
  }

  /** Trigger the hand swing animation (mining / placing / attacking). */
  swingHand(): void {
    this.held.triggerSwing();
  }

  /** Advance the held-item animation. */
  updateHeld(dt: number): void {
    this.held.update(dt);
  }

  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  /** Re-apply config values that can change at runtime (e.g. from settings). */
  applyConfig(): void {
    this.camera.fov = config.fov;
    this.camera.updateProjectionMatrix();
  }

  /** Position the camera at the player's eye, looking along yaw/pitch. */
  updateCamera(eye: Vec3, yaw: number, pitch: number): void {
    this.camera.position.set(eye.x, eye.y, eye.z);
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = yaw;
    this.camera.rotation.x = pitch;
  }

  updateSky(sky: SkyState, playerPos: Vec3): void {
    const t = sky.light;
    this.tmp.copy(NIGHT_SKY).lerp(DAY_SKY, t);
    this.scene.background = this.tmp.clone();

    const far = config.viewDistanceChunks * 16;
    this.fog.near = far * 0.45;
    this.fog.far = far;
    this.fog.color.copy(NIGHT_FOG).lerp(DAY_FOG, t);

    // Sun orbits overhead; keep it anchored near the player for stable shading.
    const r = 100;
    this.sun.position.set(
      playerPos.x + Math.cos(sky.sunAngle) * r,
      playerPos.y + Math.sin(sky.sunAngle) * r + PLAYER_EYE,
      playerPos.z + 30,
    );
    this.sun.target.position.set(playerPos.x, playerPos.y, playerPos.z);
    this.sun.intensity = 0.3 + t * 1.3;
    this.ambient.intensity = 0.35 + t * 0.75;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /** Draw calls issued in the last frame (Three.js culls off-frustum meshes). */
  get drawCalls(): number {
    return this.renderer.info.render.calls;
  }

  /** Triangles rendered in the last frame. */
  get triangles(): number {
    return this.renderer.info.render.triangles;
  }

  /** Free GPU resources when leaving the game. */
  dispose(): void {
    this.held.dispose();
    this.renderer.dispose();
  }
}
